import React, { useEffect, useState } from 'react';
import { RiArrowRightSLine } from '@remixicon/react';
import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { toast } from '@/design-system/toaster';
import {
  backOfficeDriverUrl,
  type CodOrderRow,
  type CodReconRiderRow,
  getCodOrders,
  riderWhatsAppUrl,
} from '@/services/api/codOrders';
import { formatRupiah, formatTimeInStatus } from './CodBadges';
import { OrderIdCell, type CodReconScope } from './CodBulkLegModals';

/**
 * One rider's unsettled COD, read-only, with the two confirm actions.
 *
 * It exists because the table row alone cannot answer the question an operator
 * asks before settling anything — WHICH orders, how old, and did any of them
 * come up short at Leg 1. Reading those from the row's two totals is guesswork.
 *
 * Deliberately not a third place to select and submit: selection lives in the
 * leg modals, and duplicating it here would give the same batch two
 * inconsistent entry points. This lists; the buttons act.
 */
const CodBulkRiderPanel: React.FC<{
  rider: CodReconRiderRow;
  /** Same filters as the table, so this panel lists exactly the orders the
   *  row it was opened from was counting. */
  scope: CodReconScope;
  onClose: () => void;
  onConfirmLeg1: () => void;
  onConfirmLeg2: () => void;
}> = ({ rider, scope, onClose, onConfirmLeg1, onConfirmLeg2 }) => {
  const [leg1, setLeg1] = useState<CodOrderRow[]>([]);
  const [leg2, setLeg2] = useState<CodOrderRow[]>([]);
  const [loading, setLoading] = useState(true);

  const { clientId, search, completedStartDate, completedEndDate } = scope;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const riderId = rider.riderId ?? undefined;
    const common = {
      riderId,
      clientId,
      search,
      completedStartDate,
      completedEndDate,
      limit: 200,
    };
    Promise.all([
      getCodOrders({ ...common, bucket: 'outstanding' }),
      getCodOrders({ ...common, bucket: 'pending_deposit' }),
    ])
      .then(([a, b]) => {
        if (cancelled) return;
        setLeg1(a.rows);
        setLeg2(b.rows);
      })
      .catch((error: Error) => {
        if (!cancelled) toast.error(error.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [rider.riderId, clientId, search, completedStartDate, completedEndDate]);

  const driverUrl = backOfficeDriverUrl(rider.riderId);
  const waUrl = riderWhatsAppUrl(rider.riderPhone);

  return (
    <FormModal
      open
      onClose={onClose}
      title={rider.riderName ?? rider.riderCode ?? 'Rider'}
      subtitle={`${formatRupiah(rider.leg1Amount + rider.leg2Amount)} belum selesai`}
      // RIGHT DRAWER, not a centered dialog — matching Shipments detail and
      // our own per-order COD panel, which was already a drawer. This one was
      // the odd surface out.
      //
      // A centered dialog caps at 90vh and has to hold rider identity, three
      // metrics, and two order lists that each scroll. It ran out of height
      // and the lists got 56px viewports. A drawer takes the full viewport
      // height, so the content sets its own rhythm instead of fighting the
      // fold — and it leaves room to add more per-rider context later.
      position="right"
      size="xl"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button style="stroke" onClick={onClose}>
            Tutup
          </Button>
          {/* One button per stage the rider actually has. A disabled button
              for an empty stage would suggest the action exists and is
              blocked, when in truth there is simply nothing there. */}
          {rider.leg1Count > 0 && (
            <Button style="stroke" onClick={onConfirmLeg1}>
              Confirm Leg 1 · {rider.leg1Count}
            </Button>
          )}
          {rider.leg2Count > 0 && (
            <Button onClick={onConfirmLeg2}>
              Confirm Leg 2 · {rider.leg2Count}
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        {/* Summary first, identity second. The question on opening this
            drawer is "how much is this rider carrying and across how many
            orders" — the code and phone matter only once you have decided to
            act. Status is deliberately not a third card: it lives per order in
            the Status column, where it can name the actual figure. */}
        <div className="grid grid-cols-2 gap-3">
          <SummaryCard
            label="COD dipegang"
            value={String(rider.leg1Count + rider.leg2Count)}
            hint={`${rider.leg1Count} di rider · ${rider.leg2Count} di PIC`}
          />
          <SummaryCard
            label="Total nilai"
            value={formatRupiah(rider.leg1Amount + rider.leg2Amount)}
            hint="Belum selesai, kedua leg"
          />
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-xl border border-stroke-soft-200 bg-bg-weak-50 px-4 py-3">
          {rider.riderCode && (
            <Stat
              label="Kode"
              value={
                driverUrl ? (
                  <a
                    href={driverUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-accent hover:text-accent-dark hover:underline"
                  >
                    {rider.riderCode}
                  </a>
                ) : (
                  rider.riderCode
                )
              }
            />
          )}
          {rider.riderPhone && (
            <Stat
              label="Telepon"
              value={
                waUrl ? (
                  <a
                    href={waUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-accent hover:text-accent-dark hover:underline"
                  >
                    {rider.riderPhone}
                  </a>
                ) : (
                  rider.riderPhone
                )
              }
            />
          )}
          <Stat
            label="Tertua"
            value={
              rider.oldestStageStartedAt
                ? formatTimeInStatus(rider.oldestStageStartedAt)
                : '—'
            }
          />
        </div>

        {loading ? (
          <p className="py-6 text-center text-sm text-text-sub-600">Memuat…</p>
        ) : (
          <>
            <StageList
              title="Leg 1 · Rider → PIC"
              caption="Uang masih di rider"
              rows={leg1}
            />
            <StageList
              title="Leg 2 · PIC → Finance"
              caption="Uang di PIC, belum disetor"
              rows={leg2}
              showLeg1Gap
            />
          </>
        )}
      </div>
    </FormModal>
  );
};

/** One drawer summary card. */
const SummaryCard: React.FC<{
  label: string;
  value: string;
  hint: string;
}> = ({ label, value, hint }) => (
  <div className="flex flex-col gap-0.5 rounded-xl border border-stroke-soft-200 px-4 py-3">
    <span className="text-xs text-text-sub-600">{label}</span>
    <span className="text-xl font-medium tabular-nums text-text-strong-950">
      {value}
    </span>
    <span className="text-xs text-text-sub-600">{hint}</span>
  </div>
);

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col">
    <span className="text-xs text-text-sub-600">{label}</span>
    <span className="text-sm text-text-strong-950">{value}</span>
  </div>
);

/**
 * One custody stage's orders, collapsible.
 *
 * Collapsible because a heavy rider makes this 50 rows across two stages: an
 * operator settling Leg 2 should be able to fold Leg 1 out of the way rather
 * than scroll past it. Open by default — a stage that hides its own contents on
 * arrival makes the reader work to find out whether it is empty.
 *
 * The header stays sticky while open, so forty rows down it still says which
 * stage these amounts belong to.
 */
const StageList: React.FC<{
  title: string;
  caption: string;
  rows: CodOrderRow[];
  /** Leg 2 only: compare against what the rider actually handed over. At Leg 1
   *  nothing has been measured yet, so there is no discrepancy to report. */
  showLeg1Gap?: boolean;
}> = ({ title, caption, rows, showLeg1Gap }) => {
  const [open, setOpen] = useState(true);
  const total = rows.reduce((sum, r) => sum + r.amount, 0);

  return (
    <section className="flex flex-col">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="sticky top-0 z-20 flex items-center gap-2 bg-bg-white-0 py-2 text-left"
      >
        <RiArrowRightSLine
          aria-hidden
          className={`size-5 shrink-0 text-icon-sub-600 transition-transform ${
            open ? 'rotate-90' : ''
          }`}
        />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-medium text-text-strong-950">
            {title}
          </span>
          <span className="text-xs text-text-sub-600">{caption}</span>
        </span>
        <span className="shrink-0 text-sm text-text-sub-600">
          {rows.length} pesanan · {formatRupiah(total)}
        </span>
      </button>

      {open &&
        (rows.length === 0 ? (
          <p className="rounded-xl border border-dashed border-stroke-soft-200 px-3 py-4 text-center text-xs text-text-sub-600">
            Tidak ada
          </p>
        ) : (
          <div className="flex flex-col rounded-xl border border-stroke-soft-200">
            {/* Four columns, and no more: the order's two identifiers, whose
                client it is, the COD figure, and whether that figure survived
                Leg 1. Age lives on the rider row in the table outside — per
                order it was a fifth number competing with the four that decide
                anything. */}
            <div className="sticky top-14 z-10 flex items-center gap-3 border-b border-stroke-soft-200 bg-bg-weak-50 px-3 py-1.5">
              <span className="w-52 shrink-0 text-xs font-medium text-text-sub-600">
                Pesanan · Delivery ID
              </span>
              <span className="w-24 shrink-0 text-xs font-medium text-text-sub-600">
                Client
              </span>
              <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs font-medium text-text-sub-600">
                COD
              </span>
              <span className="min-w-0 flex-1 text-xs font-medium text-text-sub-600">
                Status
              </span>
            </div>

            <div>
              {rows.map((row) => (
                <div
                  key={row.id}
                  // `items-start`: the order cell is two lines (provider
                  // reference over delivery uid) and both wrap in full rather
                  // than truncate, so there is no single baseline to centre on.
                  className="flex items-start gap-3 px-3 py-2 text-sm odd:bg-bg-weak-50/60"
                >
                  <span className="w-52 shrink-0">
                    {/* Both identifiers, unabbreviated — the same cell the
                        bulk forms use, so a row read here matches the row
                        ticked there character for character. */}
                    <OrderIdCell row={row} />
                  </span>
                  <span className="w-24 shrink-0 break-words text-xs leading-tight text-text-sub-600">
                    {row.clientName ?? '—'}
                  </span>
                  <span className="w-32 shrink-0 whitespace-nowrap text-right tabular-nums text-text-strong-950">
                    {formatRupiah(row.amount)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <StageStatus row={row} showLeg1Gap={showLeg1Gap} />
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
    </section>
  );
};

/**
 * Whether this order's money survived the leg behind it.
 *
 * At Leg 1 nothing has been measured — the rider still holds the cash — so the
 * honest answer is "not yet", not "aman". Claiming a clean result before the
 * comparison exists is how a shortfall gets discovered at Leg 2 by surprise.
 */
const StageStatus: React.FC<{
  row: CodOrderRow;
  showLeg1Gap?: boolean;
}> = ({ row, showLeg1Gap }) => {
  if (!showLeg1Gap) {
    return (
      <Badge status="faded" className="text-xs">
        Menunggu rider
      </Badge>
    );
  }
  const gap = row.discrepancyLeg1 ?? 0;
  if (gap === 0) {
    return (
      <Badge status="success" className="text-xs">
        Utuh
      </Badge>
    );
  }
  return (
    <span className="flex flex-col gap-0.5">
      <Badge status="warning" className="w-fit whitespace-nowrap text-xs">
        {gap < 0 ? 'Kurang' : 'Lebih'} {formatRupiah(Math.abs(gap))}
      </Badge>
      {/* Spelled out rather than left in a `title` tooltip: what the rider
          actually handed over is the acuan Leg 2 is measured against, so it
          decides what the next step is allowed to deposit. A figure that
          matters that much should not require hovering to see — and never
          shows at all on touch. */}
      <span className="whitespace-nowrap text-[11px] leading-tight text-text-sub-600">
        rider serahkan {formatRupiah(row.amount + gap)}
      </span>
    </span>
  );
};

export default CodBulkRiderPanel;
